import { Injectable, HostListener, Inject } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, observable, Observable, of, Subscriber, Subscription } from 'rxjs';
import { WINDOW } from "./windows.service";
import { FirestoreService } from './firestore/firestore.service';
import { AngularFireAuth } from '@angular/fire/auth';

interface Permission {
    section: number;
    name: string;
    active: boolean;
}

interface Section {
    select: boolean;
    section_name: string;
	sec?: string;
    permissions: Permission[];
}

interface UserPermissions {
    access: boolean;
    email: string;
    user_uid: string;
    sections_: Section[];
}

// Menu
export interface Menu {
	path?: string;
	title?: string;
	icon?: string;
	type?: string;
	badgeType?: string;
	badgeValue?: string;
	active?: boolean;
	bookmark?: boolean;
	children?: Menu[];
}

@Injectable({
	providedIn: 'root'
})

export class NavService {

	public screenWidth: any
	public collapseSidebar: boolean = false
	MENUITEMS: Menu[] = []

	sub1: Subscription;
	private userPermissions: UserPermissions | null = null;

	constructor(
		@Inject(WINDOW) private window, private firestore: FirestoreService, private afAuth: AngularFireAuth
	) {
		this.onResize();
		if (this.screenWidth < 991) {
			this.collapseSidebar = true
		}
	}

	// Windows width
	@HostListener("window:resize", ['$event'])
	onResize(event?) {
		this.screenWidth = window.innerWidth;
	}

	// Array
	items = new BehaviorSubject<Menu[]>(this.MENUITEMS);

	// Metodo para validar los accesos en el menu
	public async navPermissionUser(menuItems_data: any, user: any): Promise<Observable<any[]>> {
		if (!menuItems_data || !user) of([]);

		return new Promise((resolvePromise, rejectPromise) => {
			try {
				const menuObservable = new Observable<any[]>((observer) => {
					let menuItems = [];
					if (this.sub1) { this.sub1.unsubscribe(); }
			
					console.log('menuItems_data nav: ',menuItems_data);
			
					const subscriptions: Subscription[] = [];
		
					menuItems_data.forEach((item) => {
						const subscription = this.firestore.getOnePath(`nav_menu_permissions/${item.id}/users/${user.uid}`)
						.pipe(
							distinctUntilChanged((prev, curr) => {
								return JSON.stringify(prev?.sections_) === JSON.stringify(curr?.sections_) && prev?.select === curr?.select;
							})
						).subscribe((nav_user: any) => {
							let shouldAddItem = false;
			
							if (nav_user !== undefined && nav_user.select) {
								if (item.type === 'sub') {
									if (item.children && Array.isArray(item.children)) {
										let new_children = item.children.filter(child => {
											if (Array.isArray(nav_user.sections_)) {
												const page = nav_user.sections_.find(r => (r.section_name === child.title && r.select));
												return page !== undefined;
											}
											return false;
										});
			
										if (new_children.length > 0) {
											shouldAddItem = true;
											item.children = new_children;
										}
									}
								} else {
									if (item.hasOwnProperty('sections')) {
										shouldAddItem = item.sections.some(sec => {
											if (typeof sec === 'object' && Array.isArray(nav_user.sections_)) {
												const page = nav_user.sections_.find(r => (r.section_name === sec.section_name && r.select));
												return page !== undefined;
											}
											return false; 
										});
									}
								}
							}
			
							if (shouldAddItem) {
								const existingIndex = menuItems.findIndex(mi => mi.id === item.id);
			
								if (existingIndex === -1) {
									menuItems.push({
										userActive: nav_user.select,
										...item
									});
								} else {
									menuItems[existingIndex] = {
										userActive: nav_user.select,
										...item	
									};
								}
							} else {
								menuItems = menuItems.filter(mi => mi.id !== item.id);
							}
			
							console.log('menu Items new: ', menuItems);
			
							  // Emitir la lista actualizada de menuItems
							observer.next(menuItems);
						});
						subscriptions.push(subscription);
					});

					return () => { subscriptions.forEach(sub => sub.unsubscribe()); };
				}).pipe( 
					distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
				);

				resolvePromise(menuObservable);
			} catch (error) {
				rejectPromise(error);
			}
		});
	}

	setUserPermissions(permissions: UserPermissions) {
		this.userPermissions = permissions;
	}

	getUserPermissions(): UserPermissions | null {
		return this.userPermissions;
	}

	hasAccessToSection(sectionName: string): boolean {
		if (!this.userPermissions || !this.userPermissions.access) return false;

		const section = this.userPermissions.sections_.find(s => s.section_name === sectionName && s.select === true);
		return !!section;
	}

	hasAccessToSection2(sectionName: string): boolean {
		if (!this.userPermissions || !this.userPermissions.access) return false;

		const section = this.userPermissions.sections_.find(s => s.sec === sectionName && s.select === true);
		return !!section;
	}

	hasPermission(sectionName: string, permissionSection: number): boolean {
		if (!this.hasAccessToSection(sectionName)) return false;

		const section = this.userPermissions.sections_.find(s => s.section_name === sectionName);
		if (!section) return false;

		const permission = section.permissions.find(p => p.section === permissionSection);
		return permission ? permission.active : false;
	}

	hasPermission2(sectionName: string, permissionSection: number): boolean {
		if (!this.hasAccessToSection2(sectionName)) return false;

		const section = this.userPermissions.sections_.find(s => s.sec === sectionName);
		if (!section) return false;

		const permission = section.permissions.find(p => p.section === permissionSection);
		return permission ? permission.active : false;
	}

	getPermissionName(sectionName: string, permissionSection: number): string {
		if (!this.hasAccessToSection(sectionName)) return '';

		const section = this.userPermissions.sections_.find(s => s.section_name === sectionName);
		if (!section) return '';

		const permission = section.permissions.find(p => p.section === permissionSection);
		return permission ? permission.name : '';
	}

	cleanMenuItems(menuItems) {
		const seenIds = new Set(); // Set para almacenar los IDs procesados y evitar duplicados
	
		for (let i = menuItems.length - 1; i >= 0; i--) {
			const item = menuItems[i];
	
			// Si el item tiene select = false, lo eliminamos
			if (item.hasOwnProperty('userActive') && item.userActive === false) {
				menuItems.splice(i, 1);
				continue;
			}
	
			// Si el id ya ha sido procesado, verificamos si select es true
			if (item.hasOwnProperty('id')) {
				if (seenIds.has(item.id)) {
					// Si ya existe y select es true, lo reemplazamos, si no, lo eliminamos
					if (item.userActive === true) {
						const existingItemIndex = menuItems.findIndex(existingItem => existingItem.id === item.id);
						if (existingItemIndex !== -1) {
							menuItems[existingItemIndex] = item; // Reemplazamos el item duplicado
						}
					} else {
						menuItems.splice(i, 1); // Si select es false, eliminamos el item
						continue;
					}
				} else {
					seenIds.add(item.id); // Agregamos el id al set si es único
				}
			}
	
			// Procesamos los elementos de tipo 'sub' y sus hijos
			if (item.type === 'sub' && item.children && Array.isArray(item.children)) {
				for (let j = item.children.length - 1; j >= 0; j--) {
					const child = item.children[j];
	
					// Si el hijo tiene select = false, lo eliminamos
					if (child.hasOwnProperty('userActive') && child.userActive === false) {
						item.children.splice(j, 1);
						continue;
					}
	
					// Si el hijo ya ha sido procesado, lo eliminamos
					if (child.hasOwnProperty('id') && seenIds.has(child.id)) {
						item.children.splice(j, 1);
					} else if (child.hasOwnProperty('id')) {
						seenIds.add(child.id); // Agregamos el id del hijo al set si es único
					}
				}
	
				// Si el item ya no tiene hijos después de revisar, eliminamos el padre
				if (item.children.length === 0) {
					menuItems.splice(i, 1);
				}
			}
		}
	
		return menuItems;
	}
}
